import { z } from "zod";

/**
 * Error structure for document extraction
 */
const DocumentExtractionErrorSchema = z.object({
  code: z
    .enum(["INVALID_DOCUMENT_TYPE", "UNSUPPORTED_FILE_FORMAT", "EXTRACTION_SERVICE_ERROR"])
    .describe("Error code for programmatic handling"),
  message: z.string().describe("Technical error message for backend logs"),
  clientMessage: z.string().describe("User-friendly error message for mobile display"),
});

export type DocumentExtractionError = z.infer<typeof DocumentExtractionErrorSchema>;

/**
 * Atomic field schemas for document extraction
 * These can be composed into document-specific schemas
 */
export const LastNameSchema = z.string().nullable().describe("Last name");

export const FirstNameSchema = z.string().nullable().describe("First name");

export const BirthDateSchema = z.coerce.date().nullable().describe("Date of birth");

export const IssueDateSchema = z.coerce.date().nullable().describe("Issue date");

export const ExpirationDateSchema = z.coerce.date().nullable().describe("Expiration date");

export const TitleSchema = z.enum(["M", "Mme"]).nullable().describe("Title");

export const SexSchema = z.enum(["male", "female", "neutral"]).nullable().describe("Sex");

export const DocumentNumberSchema = z.string().nullable().describe("Document number");

export const IsExpiredSchema = z.boolean().describe("Whether the document is expired");

export const CountryCodeSchema = z
  .string()
  .length(2)
  .regex(/^[A-Z]{2}$/)
  .nullable()
  .describe("ISO 3166-1 alpha-2 country code (e.g., FR, DE, ES)");

export const BirthPlaceCitySchema = z.string().nullable().describe("Birth place city (Ville de naissance)");

export const BirthPlaceCountrySchema = z
  .string()
  .nullable()
  .describe("Birth place country as text (Pays de naissance) - supports historical countries/colonies");

/**
 * Outcome of the backend external-review hook, surfaced to the client
 * so it can render the correct toast after a document is stored.
 */
export const externalReviewOutcomeSchema = z.object({
  status: z.enum(["in_progress", "failed"]),
});

export type ExternalReviewOutcome = z.infer<typeof externalReviewOutcomeSchema>;

/**
 * Response schema for every document type's /store endpoint.
 * `externalReview` is present only for document types whose handler
 * ran an external review synchronously.
 */
export const storeResponseSchema = z.object({
  documentId: z.guid(),
  externalReview: externalReviewOutcomeSchema.optional(),
});

export type StoreResponse = z.infer<typeof storeResponseSchema>;

/**
 * Helper to create a discriminated union response schema for document extraction
 */
export const createDocumentExtractResponseSchema = <T extends z.ZodType>(dataSchema: T) =>
  z.discriminatedUnion("success", [
    z.object({
      success: z.literal(true).describe("Extraction succeeded"),
      data: dataSchema.describe("Extracted document data"),
    }),
    z.object({
      success: z.literal(false).describe("Extraction failed or incomplete"),
      extractedData: dataSchema.optional().describe("Partially extracted data (if any) for manual correction"),
      error: DocumentExtractionErrorSchema.describe("Extraction error details"),
    }),
  ]);
